import React, { useState } from 'react'
import { C } from '../../../shared/theme'

// Shared bits for the Cart Recovery drawer sections — the mockup's `.rd-sec`,
// `.offer-input`, `.offer-input-group label` and inline error text. The
// mockup's `--green` maps to the brand token (the palette's "green" is blue).

export const ACCENT = C.brand.primary
export const ACCENT_BG = C.brand.light

const sectionTitle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: C.gray[400],
  textTransform: 'uppercase',
  letterSpacing: '.06em',
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{
      transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
      transition: 'transform .15s',
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

// Full-bleed section: cancels SlideDrawer's 24px body padding so the divider
// spans the drawer like `.rd-sec`. `collapsible` turns the title into a
// toggle (open by default); `forceOpen` keeps it open, e.g. while the
// section shows validation errors.
export const RecoverySection: React.FC<{
  title: string
  children: React.ReactNode
  last?: boolean
  collapsible?: boolean
  forceOpen?: boolean
}> = ({ title, children, last, collapsible, forceOpen }) => {
  const [open, setOpen] = useState(true)
  const expanded = !collapsible || open || Boolean(forceOpen)
  return (
    <section
      style={{
        margin: '0 -24px',
        padding: '16px 24px',
        borderBottom: last ? 'none' : `1px solid ${C.gray[100]}`,
      }}
    >
      {collapsible ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setOpen(!expanded)}
          style={{
            ...sectionTitle,
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginBottom: expanded ? 12 : 0,
          }}
        >
          {title}
          <Chevron open={expanded} />
        </button>
      ) : (
        <div style={{ ...sectionTitle, marginBottom: 12 }}>{title}</div>
      )}
      {expanded ? children : null}
    </section>
  )
}

export const fieldLabel: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: C.gray[500],
  marginBottom: 5,
  display: 'block',
}

export const inputStyle = (invalid?: boolean): React.CSSProperties => ({
  padding: '8px 10px',
  border: `1px solid ${invalid ? C.red[700] : C.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: C.gray[800],
  outline: 'none',
  background: C.white,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
})

export const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? (
    <div style={{ fontSize: 11.5, color: C.red[700], marginTop: 4 }}>
      {message}
    </div>
  ) : null

// Selectable tile/button used by the channel + offer pickers.
export const choiceStyle = (
  selected: boolean,
  disabled?: boolean,
): React.CSSProperties => ({
  border: `${selected ? 2 : 1.5}px solid ${selected ? ACCENT : C.gray[200]}`,
  background: selected ? ACCENT_BG : C.white,
  color: selected ? ACCENT : C.gray[600],
  fontWeight: selected ? 600 : 500,
  borderRadius: 8,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  textAlign: 'center',
  fontFamily: 'inherit',
  transition: 'all .15s',
})
